import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * What every landing demo needs to play: is the reader here, do they want motion, and which beat.
 *
 * `AgentAsksDemo` and `ContextPulledDemo` each grew their own copy of the first two; the third row
 * is where they come out (Beka, 2026-09-12). The semantics are `AgentAsksDemo`'s exactly, including
 * the one that matters most — a demo that is not playing shows its LAST beat, not its first. An
 * off-screen or reduced-motion reader gets the finished state, which is the frame worth holding.
 */

export function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduce(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

/**
 * Whether the host element is on screen at all.
 *
 * `threshold: 0` on purpose: a row bleeding off the panel's edge is "here" the moment any of it is,
 * and waiting for a fraction of a tall panel would start the run after the reader has read it.
 */
export function useInView<T extends Element>(): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
}

/**
 * Step through `beats`, holding each for its own dwell, looping forever while `play`.
 *
 * One timer at a time rather than an interval, so a beat's length is its own business and a tab that
 * sleeps does not wake up owing the sequence four transitions.
 */
export function useBeat<T extends string>(beats: readonly T[], dwell: Record<T, number>, play: boolean): T {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!play) return;
    const timer = window.setTimeout(() => setIndex((i) => (i + 1) % beats.length), dwell[beats[index]]);
    return () => window.clearTimeout(timer);
  }, [play, index, beats, dwell]);
  return play ? beats[index] : beats[beats.length - 1];
}
